/**
 * Category Tap content: types, level params, round sequence builder from schedule + items.
 * Spec: items one at a time; category switches mid-round; TAP if belongs / SKIP.
 */

export interface CategoryTapItem {
  id: string;
  label: string;
  imageUrl: string;
  categories: string[];
}

export interface CategoryTapPhase {
  category: string;
  duration: number;
  compound?: { attribute: string; value: string };
}

export interface CategoryTapSchedule {
  scheduleId: string;
  phases: CategoryTapPhase[];
}

/** Backend returns { schedule, items } for category-tap */
export interface CategoryTapContentResponse {
  schedule?: CategoryTapSchedule;
  items?: CategoryTapItem[] | Record<string, unknown>[];
}

export interface CategoryTapLevelParams {
  /** Total items per round (cap; schedule may request fewer) */
  maxItems: number;
  /** Ms per item before auto-advance (0 = no auto-advance) */
  timePerItemMs: number;
  showLabels: boolean;
  showImage: boolean;
}

export interface StimulusStep {
  item: CategoryTapItem;
  phaseIndex: number;
  category: string;
  /** True if item belongs to current phase category */
  belongsToCategory: boolean;
}

export interface CategoryTapRoundContent {
  sequence: StimulusStep[];
  levelParams: CategoryTapLevelParams;
  schedule: CategoryTapSchedule;
}

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function shuffleWithSeed<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Level params from spec table: 1–2 single category 10 items; 3–4 one switch 15; 5–6 two switches 18; 7–8 two–three 20; 9–10 three switches 24, 1.5s per item */
export function getLevelParams(difficulty: number): CategoryTapLevelParams {
  const level = Math.max(1, Math.min(10, difficulty));
  if (level <= 2) {
    return {
      maxItems: 10,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 4) {
    return {
      maxItems: 15,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 6) {
    return {
      maxItems: 18,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 8) {
    return {
      maxItems: 20,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  return {
    maxItems: 24,
    timePerItemMs: 1700,
    showLabels: true,
    showImage: true,
  };
}

function normalizeItem(raw: Record<string, unknown>): CategoryTapItem {
  const id = (raw.id ?? raw.itemId ?? "") as string;
  return {
    id,
    label: (raw.label as string) ?? "",
    imageUrl: (raw.imageUrl as string) ?? "",
    categories: Array.isArray(raw.categories) ? (raw.categories as string[]) : [],
  };
}

function itemBelongsToCategory(item: CategoryTapItem, category: string): boolean {
  return item.categories.some((c) => c.toLowerCase() === category.toLowerCase());
}

/**
 * Build the round sequence from schedule and item pool.
 * For each phase: pick `duration` items (mix of in-category and distractors), shuffle within phase.
 */
export function buildRoundSequence(
  schedule: CategoryTapSchedule,
  items: CategoryTapItem[],
  seed: string,
  roundIndex: number
): StimulusStep[] {
  const rng = seededRandom(`${seed}-${roundIndex}`);
  const steps: StimulusStep[] = [];
  const usedIds = new Set<string>();

  for (let phaseIndex = 0; phaseIndex < schedule.phases.length; phaseIndex++) {
    const phase = schedule.phases[phaseIndex];
    const category = phase.category;
    const duration = phase.duration;

    const inCategory = items.filter(
      (i) => !usedIds.has(i.id) && itemBelongsToCategory(i, category)
    );
    const notInCategory = items.filter(
      (i) => !usedIds.has(i.id) && !itemBelongsToCategory(i, category)
    );

    const numTarget = Math.max(
      1,
      Math.min(inCategory.length, Math.floor(duration * (0.5 + rng() * 0.3)))
    );
    const numDistractor = Math.min(duration - numTarget, notInCategory.length);
    const totalThisPhase = numTarget + numDistractor;

    const chosenIn = shuffleWithSeed(inCategory, rng).slice(0, numTarget);
    const chosenOut = shuffleWithSeed(notInCategory, rng).slice(0, numDistractor);
    const phaseSteps = shuffleWithSeed([...chosenIn, ...chosenOut], rng);

    for (const item of phaseSteps) {
      usedIds.add(item.id);
      steps.push({
        item,
        phaseIndex,
        category,
        belongsToCategory: itemBelongsToCategory(item, category),
      });
    }
  }

  return steps;
}

function isCategoryTapContentResponse(
  data: unknown
): data is CategoryTapContentResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  const schedule = d.schedule as Record<string, unknown> | undefined;
  if (!schedule || !Array.isArray(schedule?.phases)) return false;
  return true;
}

/**
 * Resolve round content from API response. Normalizes items (id/itemId), picks schedule, builds sequence.
 */
export function resolveRoundContent(
  contentData: unknown,
  contentError: unknown,
  contentSeed: string,
  roundIndex: number,
  levelParams: CategoryTapLevelParams,
  sessionSeed?: string
): CategoryTapRoundContent | null {
  const effectiveSeed = sessionSeed
    ? (contentSeed ? `${contentSeed}-${sessionSeed}` : sessionSeed)
    : contentSeed || `local-${roundIndex}-${Date.now()}`;

  if (contentError || !isCategoryTapContentResponse(contentData)) {
    return null;
  }

  const rawItems = contentData.items;
  const rawSchedule = contentData.schedule;
  if (!rawSchedule || !Array.isArray(rawItems) || rawItems.length < 5) {
    return null;
  }

  const items = (rawItems as Record<string, unknown>[]).map(normalizeItem);
  const schedule: CategoryTapSchedule = {
    scheduleId: (rawSchedule.scheduleId as string) ?? "fallback",
    phases: (rawSchedule.phases as CategoryTapPhase[]) ?? [],
  };

  if (schedule.phases.length === 0) {
    return null;
  }

  const sequence = buildRoundSequence(
    schedule,
    items,
    effectiveSeed,
    roundIndex
  ).slice(0, levelParams.maxItems);

  if (sequence.length === 0) {
    return null;
  }

  return {
    sequence,
    levelParams,
    schedule,
  };
}

/**
 * Compute accuracy (correct / total) and switchScore (accuracy on first 3 items after each category switch).
 */
export function computeRoundMetrics(
  sequence: StimulusStep[],
  responses: { tapped: boolean; missed?: boolean }[]
): { accuracy: number; switchScore: number; missedItems: number } {
  let correct = 0;
  let total = 0;
  let missedItems = 0;
  const postSwitchScores: number[] = [];
  let phaseStartIndex = 0;

  for (let i = 0; i < sequence.length; i++) {
    const step = sequence[i];
    const resp = responses[i];
    if (!resp) {
      missedItems++;
      total++;
      continue;
    }
    if (resp.missed) {
      missedItems++;
      total++;
      continue;
    }
    const expectedTap = step.belongsToCategory;
    const actualTap = resp.tapped;
    if (expectedTap === actualTap) correct++;
    total++;

    // Detect phase switch: next step has different phaseIndex
    const nextStep = sequence[i + 1];
    if (nextStep && nextStep.phaseIndex !== step.phaseIndex) {
      const window = responses.slice(i + 1, Math.min(i + 4, responses.length));
      const windowSteps = sequence.slice(i + 1, i + 1 + window.length);
      let windowCorrect = 0;
      let windowTotal = 0;
      for (let w = 0; w < window.length && w < windowSteps.length; w++) {
        if (window[w]?.missed) {
          windowTotal++;
          continue;
        }
        if (window[w]) {
          windowTotal++;
          const exp = windowSteps[w].belongsToCategory;
          const act = window[w].tapped;
          if (exp === act) windowCorrect++;
        }
      }
      if (windowTotal > 0) {
        postSwitchScores.push(windowCorrect / windowTotal);
      }
    }
  }

  const accuracy = total > 0 ? correct / total : 0;
  const switchScore =
    postSwitchScores.length > 0
      ? postSwitchScores.reduce((a, b) => a + b, 0) / postSwitchScores.length
      : accuracy;

  return { accuracy, switchScore, missedItems };
}
